<template>
  <div class="notification-settings-panel mb-4">
    <h2 class="mt-4 text-primary mb-4">
      Your notification settings for {{ ecosystemDisplayName }}
    </h2>
    <p
      class="form-group__label__text mt-4"
      style="margin-bottom: 0.5rem; text-align: left"
      v-if="isOwner && !isSlackEnabled"
    >
      To send notifications via Slack, configure your webhook
      <a href="/settings/integrations">here</a>.
    </p>

    <table class="notification-settings-panel__settings-table">
      <tbody v-for="(category, index) in displayedCategories">
      <tr>
        <th class="label-column">{{ category.label }} {{ $t('generic_notifications') }}</th>
        <th class="website-column">{{ $t('notification_settings_in_app') }}</th>
        <th class="email-column">{{ $t('notification_settings_email') }}</th>
        <th class="slack-column" v-if="showSlackSettings">Slack</th>
      </tr>
      <tr>
        <td>&nbsp;</td>
      </tr>
      <tr v-for="notification in category.notifications">
        <template v-if="updatedNotificationsUserObject.settings.notifications[notification.name]">
          <td class="label-column">{{ notification.label }}</td>
          <td class="website-column">
            <toggle-button
              v-model="updatedNotificationsUserObject.settings.notifications[notification.name].database.enabled"
              :enabled="updatedNotificationsUserObject.settings.notifications[notification.name].database.enabled"
              color="success"
            />
          </td>
          <td class="email-column">
            <div v-if="notification.name === 'user_invited' || notification.name === 'user_account_activated'">
              <dropdown
                is-simplified
                :options="[{ 'label': 'On', 'value': 'instant' }, { 'label': 'Off', 'value': 'none' }]"
                :modelValue="getEmailValue(notification.name)"
                @update:modelValue="setEmailValue(notification.name, $event)"
              />
            </div>
            <dropdown
              v-else
              is-simplified
              :options="notification.notificationOptions"
              :modelValue="getEmailValue(notification.name)"
              @update:modelValue="setEmailValue(notification.name, $event)"
            />
          </td>
          <td class="slack-column" v-if="showSlackSettings">
            <toggle-button
              v-model="config.notifications[notification.name].slack.enabled"
              :enabled="config.notifications[notification.name].slack.enabled"
              color="success"
            />
          </td>
        </template>
      </tr>
      </tbody>

      <tbody v-if="displayCommunityDigestNotificationSettings">
      <tr>
        <th class="label-column">Community digests</th>
        <th class="website-column"></th>
        <th class="email-column"></th>
        <th class="slack-column" v-if="showSlackSettings"></th>
      </tr>
      <tr>
        <td>&nbsp;</td>
      </tr>
      <tr v-for="community in joinedCommunities">
        <td class="label-column">{{ community.name }}</td>
        <td class="website-column">&nbsp;</td>
        <td class="email-column">
          <dropdown
            is-simplified
            :options="emailOptionsOnlyWeekly"
            :model-value="communityDigestNotifications[community.id]"
            @update:modelValue="updateCommunityDigestNotification(community.id, $event)"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      </tbody>

      <tbody>
      <tr>
        <th class="label-column">{{ $t('notification_settings_weekly_digest') }}</th>
        <th class="website-column"></th>
        <th class="email-column"></th>
        <th class="slack-column" v-if="showSlackSettings"></th>
      </tr>
      <tr>
        <td>&nbsp;</td>
      </tr>

      <tr v-if="isMember && hasAccessToPublisher">
        <td class="label-column">{{ $t('notification_settings_curation_digest') }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <dropdown
            is-simplified
            :options="emailOptions"
            v-model="curationDigest"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      <tr>
        <td class="label-column">{{ $t('notification_settings_activities_digest') }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <dropdown
            is-simplified
            :options="emailOptionsOnlyWeekly"
            v-model="weeklyDigest"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      <tr v-if="hasAccessToMonitoring">
        <td class="label-column">{{ $t('notification_settings_articles_digest') }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <dropdown
            is-simplified
            :options="emailOptions"
            v-model="contentDigest"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      </tbody>

      <tbody v-if="$store.getters.isPublisherEnabled">
      <tr>
        <th class="label-column">{{ $t('permissions_email_subscriptions') }}</th>
        <th class="website-column"></th>
        <th class="email-column"></th>
        <th class="slack-column" v-if="showSlackSettings"></th>
      </tr>
      <tr>
        <td>&nbsp;</td>
      </tr>
      <tr>
        <td class="label-column">{{ $t('ecosystem_notification_permission_updates', { ecosystem: ecosystemDisplayName }) }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <toggle-button
            v-model="userUpdatesAndFeatures"
            :enabled="userUpdatesAndFeatures"
            color="success"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      <tr>
        <td class="label-column">{{ $t('ecosystem_notification_permission_blogposts', { ecosystem: ecosystemDisplayName }) }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <toggle-button
            v-model="userBlogPosts"
            :enabled="userBlogPosts"
            color="success"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      <tr>
        <td class="label-column">{{ $t('ecosystem_notification_permission_events', { ecosystem: ecosystemDisplayName }) }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <toggle-button
            v-model="userEventInvites"
            :enabled="userEventInvites"
            color="success"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      <tr v-if="isMember">
        <td class="label-column">{{ $t('ecosystem_notification_permission_beta', { ecosystem: ecosystemDisplayName }) }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <toggle-button
            v-model="userBetaInvites"
            :enabled="userBetaInvites"
            color="success"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      <tr v-if="config.newsletter">
        <td class="label-column">{{ $t('permissions_email_subscriptions_permission', { ecosystem: ecosystemDisplayName }) }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <toggle-button
            v-model="userNewsletter"
            :enabled="userNewsletter"
            color="success"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      <tr v-if="isMember">
        <td class="label-column">{{ $t('ecosystem_notification_permission_datascouts_newsletter', { ecosystem: ecosystemDisplayName }) }}</td>
        <td class="website-column"></td>
        <td class="email-column">
          <toggle-button
            v-model="userDataScoutsNewsletter"
            :enabled="userDataScoutsNewsletter"
            color="success"
          />
        </td>
        <td class="slack-column" v-if="showSlackSettings"></td>
      </tr>
      <tr>
        <td colspan="4" class="label-column" style="font-size: 10px">
          {{ $t('permissions_disclaimer') }}
        </td>
      </tr>
      </tbody>
    </table>

    <!-- Auto-save Status -->
    <div v-if="saving" class="fixed bottom-4 right-4 bg-blue-100 border border-blue-300 text-blue-800 px-4 py-2 rounded-lg shadow-lg">
      <div class="tw-flex items-center space-x-2">
        <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-sm">Saving...</span>
      </div>
    </div>

    <div v-else-if="lastSaved" class="fixed bottom-4 right-4 bg-green-100 border border-green-300 text-green-800 px-4 py-2 rounded-lg shadow-lg opacity-75">
      <div class="tw-flex items-center space-x-2">
        <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
        <span class="text-sm">Saved {{ formatTimeAgo(lastSaved) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
  import Dropdown from '../Dropdown/Dropdown.vue'
  import ToggleButton from '../Form/ToggleButton.vue'
  import TranslationsMixin from '../../util/TranslationsMixin'
  import NotificationSettingConfig from '../../config/notificationSettings'
  import CommunityMixin from '@/util/CommunityMixin'
  import ConceptSearchMixin from '../../util/ConceptSearchMixin'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui'
  import { MUTATION_TYPES as USER_MUTATION_TYPES } from '../../store/modules/user-helpers.js'
  import MODAL_IDS from '../../constants/modal-ids'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import isEqual from 'lodash/isEqual'
  import debounce from 'lodash/debounce'
  import { prepareConfigForEditing, removeDefaultsFrom } from '@/util/config'
  import { updateProfile } from '@/api/user'
  import { updateConfig } from '@/api/config'

  export default {
    name: 'NotificationSettings',
    mixins: [TranslationsMixin, CommunityMixin, ConceptSearchMixin],
    components: {
      Dropdown,
      ToggleButton,
      DsButton,
    },
    props: {},
    emits: ['settings-updated'],
    data () {
      return {
        updatedNotificationsUserObject: {},
        config: {},
        saving: false,
        lastSaved: null,
        saveTimer: null,
        emailOptionsOnlyWeekly: [
          { label: this.$t('notification_settings_off'), value: 'none' },
          { label: this.$t('notification_settings_weekly'), value: 'yes' },
        ],
        emailOptionsWithInstant: [
          { label: this.$t('notification_settings_off'), value: 'none' },
          { label: this.$t('notification_settings_instant'), value: 'instant' },
          { label: this.$t('notification_settings_daily'), value: 'daily' },
          { label: this.$t('notification_settings_weekly'), value: 'weekly' },
        ],
        emailOptions: [
          { label: this.$t('notification_settings_off'), value: 'none' },
          { label: this.$t('notification_settings_daily'), value: 'daily' },
          { label: this.$t('notification_settings_weekly'), value: 'weekly' },
        ],
        emailOptionsOnlyInstant: [
          { label: this.$t('notification_settings_off'), value: 'none' },
          { label: this.$t('notification_settings_instant'), value: 'instant' },
        ],
      }
    },
    computed: {
      displayCommunityDigestNotificationSettings () {
        return this.$store.getters.hasAccessToCommunities && this.joinedCommunities.length > 0
      },
      isMember () {
        return this.$store.getters.isMember
      },
      hasAccessToMonitoring () {
        return this.$store.getters.hasAccessToMonitoring
      },
      hasAccessToPublisher () {
        return this.$store.getters.hasAccessToPublisher
      },
      showSlackSettings () {
        return this.isOwner && this.config.slack && this.config.slack.endpoint
      },
      isOwner () {
        return this.$store.getters.isOwner
      },
      isSlackEnabled () {
        return this.config.slack && this.config.slack && this.config.slack.endpoint
      },
      ecosystemDisplayName () {
        return this.$store.getters.ecosystemDisplayName
      },
      communityDigestNotifications () {
        let communityDigestNotifications = this.updatedNotificationsUserObject.settings?.communityDigestNotifications || {}

        this.joinedCommunities.forEach(community => {
          if (!communityDigestNotifications[community.id]) {
            communityDigestNotifications[community.id] = 'weekly'
          }
        })

        return communityDigestNotifications
      },
      displayedCategories () {
        if (!this.updatedNotificationsUserObject || !this.updatedNotificationsUserObject.settings) {
          return []
        }

        const userNotificationSettings = this.updatedNotificationsUserObject.settings.notifications
        const ecosystemNotificationSettings = this.config.notifications
        const result = []

        for (const category of NotificationSettingConfig) {
          const resultCategory = {
            label: category.label === 'Announcement' ? this.announcementLabel : category.label,
            notifications: [],
          }

          for (const notification of category.notifications) {
            if (userNotificationSettings[notification.name] === undefined) {
              continue
            }

            if (this.showSlackSettings && ecosystemNotificationSettings[notification.name] === undefined) {
              continue
            }

            let notificationOptions = [...this.emailOptions]

            if (category.label === 'Conversation') {
              notificationOptions = [...this.emailOptionsWithInstant]
            }

            if (notification.name === 'actor_claim_needs_curation') {
              notificationOptions = [...this.emailOptionsOnlyInstant]
            }

            resultCategory.notifications.push({
              name: notification.name,
              notificationOptions: notificationOptions,
              label: notification.label.indexOf('_') >= 0 ? this.$t(notification.label, {
                announcements: this.announcementsLabel,
                announcement: this.announcementLabel,
              }) : notification.label,
            })
          }

          if (resultCategory.notifications.length > 0) {
            result.push(resultCategory)
          }
        }

        // If challenges are enabled, display the settings for challenges
        if (this.$store.getters.areChallengesEnabled) {
          const challengesNotifications = {
            label: this.challengesLabel,
            notifications: [
              {
                name: 'new_public_challenge',
                label: this.$t('notification_new_challenge', { challenge: this.challengeLabel }),
                notificationOptions: this.emailOptions
              },
              {
                name: 'new_challenge_comment',
                label: this.$t('notification_challenge_comment', { challenges: this.challengesLabel }),
                notificationOptions: this.emailOptions
              },
              {
                name: 'challenge_pinned',
                label: this.$t('notification_challenge_pinned', { challenge: this.challengeLabel }),
                notificationOptions: this.emailOptions
              },
              {
                name: 'challenge_mentioned_actor',
                label: this.$t('notification_challenge_mentioned'),
                notificationOptions: this.emailOptions
              },
            ],
          }

          result.push(challengesNotifications)
        }

        return result
      },
      weeklyDigest: {
        get () {
          const setting = (this.updatedNotificationsUserObject.settings || {}).weeklyDigest
          const validOptions = this.emailOptionsOnlyWeekly.map(option => option.value)
          return validOptions.includes(setting) ? setting : 'yes'
        },
        set (v) {
          this.updatedNotificationsUserObject.settings.weeklyDigest = v
          this.debouncedAutoSave()
        },
      },
      curationDigest: {
        get () {
          const setting = (this.updatedNotificationsUserObject.settings || {}).curationDigest
          return !setting ? 'daily' : setting
        },
        set (v) {
          this.updatedNotificationsUserObject.settings.curationDigest = v
          this.debouncedAutoSave()
        },
      },
      contentDigest: {
        get () {
          const setting = (this.updatedNotificationsUserObject.settings || {}).contentDigest
          return setting || 'none'
        },
        set (v) {
          if (this.contentDigest === 'none' && v !== 'none' && !this.isFollowingSearches) {
            this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONTENT_DIGEST_INFO)
          }
          this.updatedNotificationsUserObject.settings.contentDigest = v
          this.debouncedAutoSave()
        },
      },
      userNewsletter: {
        get () {
          return !!(this.updatedNotificationsUserObject.settings || {}).newsletter
        },
        set (v) {
          this.updatedNotificationsUserObject.settings.newsletter = v ? 'yes' : ''
          this.debouncedAutoSave()
        },
      },
      userUpdatesAndFeatures: {
        get () {
          return !!(this.updatedNotificationsUserObject.settings || {}).updatesAndFeatures
        },
        set (v) {
          this.updatedNotificationsUserObject.settings.updatesAndFeatures = v ? 'yes' : ''
          this.debouncedAutoSave()
        },
      },
      userDataScoutsNewsletter: {
        get () {
          return !!(this.updatedNotificationsUserObject.settings || {}).dataScoutsNewsletter
        },
        set (v) {
          this.updatedNotificationsUserObject.settings.dataScoutsNewsletter = v ? 'yes' : ''
          this.debouncedAutoSave()
        },
      },
      userBlogPosts: {
        get () {
          return !!(this.updatedNotificationsUserObject.settings || {}).blogPosts
        },
        set (v) {
          this.updatedNotificationsUserObject.settings.blogPosts = v ? 'yes' : ''
          this.debouncedAutoSave()
        },
      },
      userEventInvites: {
        get () {
          return !!(this.updatedNotificationsUserObject.settings || {}).eventInvites
        },
        set (v) {
          this.updatedNotificationsUserObject.settings.eventInvites = v ? 'yes' : ''
          this.debouncedAutoSave()
        },
      },
      userBetaInvites: {
        get () {
          return !!(this.updatedNotificationsUserObject.settings || {}).betaInvites
        },
        set (v) {
          this.updatedNotificationsUserObject.settings.betaInvites = v ? 'yes' : ''
          this.debouncedAutoSave()
        },
      },
      isEditing () {
        return !isEqual(this.updatedNotificationsUserObject.settings, this.currentUser().settings)
      },
      isFollowingSearches () {
        // Access followedConceptSearchIds from ConceptSearchMixin if needed
        // For now, we'll use a simple check or pass it as a prop
        return this.followedConceptSearchIds ? this.followedConceptSearchIds.length > 0 : false
      },
    },
    methods: {
      currentUser () {
        const profile = this.$store.state.user.profile
        return this.prepareUserForEditing(profile)
      },
      formatTimeAgo (timestamp) {
        if (!timestamp) return ''
        const now = new Date()
        const saved = new Date(timestamp)
        const seconds = Math.floor((now - saved) / 1000)

        if (seconds < 30) return 'just now'
        if (seconds < 60) return `${seconds}s ago`
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
        return `${Math.floor(seconds / 3600)}h ago`
      },
      prepareUserForEditing (profile) {
        // Simple version - in real app this would be imported from util/user-settings
        return JSON.parse(JSON.stringify(profile))
      },
      initializeData () {
        this.updatedNotificationsUserObject = this.currentUser()
        if (this.$store.getters.isMember) {
          this.fetchAndPrepareConfigForEditing().then(config => {
            this.config = config
          })
        } else {
          this.config = prepareConfigForEditing(this.$store.state.config)
        }
      },
      async fetchAndPrepareConfigForEditing () {
        // Simple version - in real app this would be imported from util/config
        return prepareConfigForEditing(this.$store.state.config)
      },
      updateCommunityDigestNotification (communityId, value) {
        this.updatedNotificationsUserObject.settings.communityDigestNotifications[communityId] = value
        this.debouncedAutoSave()
      },
      getEmailValue (notificationName) {
        return this.updatedNotificationsUserObject.settings.notifications[notificationName].email.digest
      },
      setEmailValue (notificationName, value) {
        this.updatedNotificationsUserObject.settings.notifications[notificationName].email.enabled = (value !== 'none')
        this.updatedNotificationsUserObject.settings.notifications[notificationName].email.digest = value
        this.debouncedAutoSave()
      },
      debouncedAutoSave: debounce(function () {
        this.autoSave()
      }, 1000),

      async autoSave () {
        if (!this.isEditing || this.saving) {
          return
        }

        this.saving = true
        const promises = []

        // Apply changes to user settings
        promises.push(
          updateProfile({ settings: this.updatedNotificationsUserObject.settings })
            .then(data => {
              this.$store.commit(USER_MUTATION_TYPES.UPDATE_PROFILE, data)
              this.lastSaved = new Date()
              this.saving = false
              this.$emit('settings-updated')

              // Hide success message after 3 seconds
              setTimeout(() => {
                this.lastSaved = null
              }, 3000)
            })
            .catch(error => {
              this.saving = false
              console.error('Failed to save notification settings:', error)
            })
        )

        if (this.showSlackSettings) {
          // Apply changes to ecosystem config
          const formData = removeDefaultsFrom(this.config, {})
          delete formData.possibleCategories
          delete formData.categories
          delete formData.hexBorders
          delete formData.colors

          promises.push(
            updateConfig(formData)
              .then(config => {
                this.$store.commit('CONFIG_SET', config)
                this.config = config
              })
              .catch(error => {
                console.error('Failed to save config:', error)
              })
          )
        }

        try {
          await Promise.all(promises)
        } catch (e) {
          console.error('Error saving notification settings:', e)
          this.saving = false
        }
      },
    },
    mounted () {
      this.initializeData()
    },
    watch: {
      // Watch for changes from the store and re-initialize if needed
      '$store.state.user.profile': {
        handler () {
          this.initializeData()
        },
        deep: true
      },
      // Watch for changes to notification settings and auto-save
      'updatedNotificationsUserObject.settings': {
        handler () {
          if (this.isEditing) {
            this.debouncedAutoSave()
          }
        },
        deep: true
      },
      // Watch for specific settings changes
      weeklyDigest () {
        this.debouncedAutoSave()
      },
      curationDigest () {
        this.debouncedAutoSave()
      },
      contentDigest () {
        this.debouncedAutoSave()
      },
      userNewsletter () {
        this.debouncedAutoSave()
      },
      userUpdatesAndFeatures () {
        this.debouncedAutoSave()
      },
      userDataScoutsNewsletter () {
        this.debouncedAutoSave()
      },
      userBlogPosts () {
        this.debouncedAutoSave()
      },
      userEventInvites () {
        this.debouncedAutoSave()
      },
      userBetaInvites () {
        this.debouncedAutoSave()
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";

  .notification-settings-panel {

    .notification-settings-panel__settings-table {
      border-collapse: collapse;
      margin: 0 auto;

      th {
        border-bottom: 1px solid var(--primary-lightest);
        color: var(--primary);
      }

      td.label-column, td.website-column, td.label-column, td.slack-column, td.email-column {
        padding-bottom: 5px;
        padding-top: 5px;
      }
    }
  }

  :deep(.form-group__label__text) {
    color: var(--primary);
  }
</style>
